import { useMemo } from "react";
import { useQuery } from "react-query";

import type { ArticleAEMData } from "@jti/fe-common";
import { groupBy } from "@jti/fe-common";
import type { LoyaltyEarningType } from "@jti/rest-api-types";

import type { LoyaltyApiContact } from "../../../services";
import { getUser } from "../../../utils/User";

const LOYALTY_EARNING_TYPES: readonly string[] = ["article", "product", "video", "quiz"];

const isLoyaltyEarningType = (value: string): value is LoyaltyEarningType => LOYALTY_EARNING_TYPES.includes(value);

export const useBlogListingLoyaltyLogic = (
  isInEditor: boolean | undefined,
  disableLoyalty: boolean | undefined,
  disableTiers: boolean | undefined,
  articles: ArticleAEMData[],
  featuredArticle: ArticleAEMData,
  loyaltyApi: LoyaltyApiContact
) => {
  const user = getUser();
  const { uuid: userUuid, ciam_token: userCiamToken } = user || {};
  const isUserLoggedIn = Boolean(userUuid && userCiamToken);
  const shouldFetchLoyaltyData = !isInEditor && !disableLoyalty && !disableTiers;
  const groupedArticles = useMemo(() => groupBy(articles, (article) => article.articleType || "article"), [articles]);

  const { isError, isLoading, data } = useQuery(
    [userUuid, userCiamToken, JSON.stringify(groupedArticles)],
    async () => {
      const typedIdentifiers = Object.entries(groupedArticles).reduce<Partial<Record<LoyaltyEarningType, string[]>>>(
        (acc, [articleType, items]) => {
          if (isLoyaltyEarningType(articleType)) {
            acc[articleType] = items.map((item) => item.articleId).filter((id): id is string => id != null);
          }
          return acc;
        },
        {}
      );

      const articlesMetaData = await loyaltyApi.getArticles({
        uuid: userUuid,
        ciam_token: userCiamToken,
        typedIdentifiers,
      });

      const featuredMetaData = articlesMetaData.find(
        ({ article_type, article_id }) =>
          article_type === featuredArticle.articleType && article_id === featuredArticle?.articleId
      );

      return {
        articlesMetaData,
        featuredMetaData,
      };
    },
    {
      enabled: shouldFetchLoyaltyData,
    }
  );

  const { articlesMetaData = [], featuredMetaData } = data || {};

  return {
    hasError: isError,
    isLoading,
    isLogged: isUserLoggedIn,
    articlesMetaData,
    featuredMetaData,
  };
};
